import { cn } from 'lib/utils'
import { ChevronDown } from 'lucide-react'
import {
  BrandMemoriesList,
  type BrandMemoriesListHandle,
} from 'modules/creative/components/BrandMemoriesList'
import { useEffect, useRef, type RefObject } from 'react'
import {
  getProgressVisualProps,
  SKIP_LABEL,
  type InterviewChromeState,
} from './conversation-chrome'
import { LogoStepProgress } from './LogoStepProgress'

export interface ConversationProgressHubProps {
  chrome: InterviewChromeState
  memoriesRef: RefObject<BrandMemoriesListHandle | null>
  businessReady: boolean
  listExpanded: boolean
  onToggleList: () => void
  listFlash?: boolean
  onCountChange: (count: number) => void
  onSkip?: () => void
}

const SAVED_TOP = 'calc(42% + 98px)'
const SKIP_RESERVE = '3.5rem'

const mutedBodyClass = 't-body text-paper-dim'

const savedLabelClass = (flash: boolean) =>
  cn(
    't-body inline-flex items-center gap-1.5 font-medium transition-colors duration-300 hover:text-paper',
    flash ? 'text-acid' : 'text-paper-dim'
  )

export function ConversationProgressHub({
  chrome,
  memoriesRef,
  businessReady,
  listExpanded,
  onToggleList,
  listFlash,
  onCountChange,
  onSkip,
}: ConversationProgressHubProps) {
  const visual = getProgressVisualProps(chrome)
  const { savedCount } = chrome
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!listFlash || !listExpanded || savedCount <= 1) return
    const el = listRef.current
    if (!el) return

    let step = 0
    const rows = () => Array.from(el.querySelectorAll<HTMLElement>('[data-memory-row]'))
    const tick = () => {
      const items = rows()
      if (items.length === 0) return
      const rowH = items[0]?.offsetHeight ?? 28
      el.scrollTo({
        top: Math.min(step * rowH, el.scrollHeight - el.clientHeight),
        behavior: 'smooth',
      })
      step = step >= items.length - 1 ? 0 : step + 1
    }

    tick()
    const id = window.setInterval(tick, 700)
    return () => window.clearInterval(id)
  }, [listFlash, listExpanded, savedCount])

  return (
    <div className="relative min-h-0 w-full max-w-[320px] flex-1 px-2 py-4">
      <div className="absolute top-[42%] left-1/2 z-10 h-[140px] w-[140px] -translate-x-1/2 -translate-y-1/2">
        <LogoStepProgress
          step={visual.step}
          animate={visual.animate}
          halo={visual.halo}
          smoothFill={visual.smoothFill}
        />
      </div>

      <div
        className="absolute left-1/2 z-20 flex w-[min(280px,calc(100%-1rem))] -translate-x-1/2 flex-col items-center overflow-hidden"
        style={{ top: SAVED_TOP, bottom: SKIP_RESERVE }}
      >
        <div className="flex shrink-0 flex-col items-center">
          {savedCount === 0 ? (
            <span className={mutedBodyClass}>Unsaved</span>
          ) : (
            <button type="button" onClick={onToggleList} className={savedLabelClass(!!listFlash)}>
              {savedCount} saved
              <ChevronDown
                size={14}
                className={cn(
                  'shrink-0 transition-transform duration-200',
                  listExpanded && 'rotate-180'
                )}
              />
            </button>
          )}
        </div>

        <div
          className={cn(
            'relative mt-2 flex min-h-0 w-full flex-1 flex-col transition-opacity duration-300',
            (!listExpanded || savedCount === 0) && 'pointer-events-none opacity-0',
            listExpanded && savedCount > 0 && 'opacity-100'
          )}
        >
          <div
            ref={listRef}
            className="min-h-0 flex-1 overflow-y-auto text-left [scrollbar-width:thin]"
          >
            <BrandMemoriesList
              ref={memoriesRef}
              appearance="bullet"
              enabled={businessReady}
              onCountChange={onCountChange}
              readOnly
            />
          </div>
        </div>
      </div>

      {onSkip && !chrome.doneActive && (
        <button
          type="button"
          onClick={() => onSkip()}
          className="link-muted t-body absolute bottom-4 left-1/2 -translate-x-1/2"
        >
          {SKIP_LABEL}
        </button>
      )}
    </div>
  )
}
